import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Avatar, Segmented, Tooltip } from "antd";
import {
  PlusOutlined,
  CloseOutlined,
  QuestionCircleOutlined,
  ThunderboltOutlined,
  AppstoreOutlined,
  HolderOutlined,
} from "@ant-design/icons";
import AddShortcutModal from "./AddShortcutModal";
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  loadQuickAccess,
  saveQuickAccess,
  COLUMN_OPTIONS,
} from "../../utils/quickAccess";
import { resolveSource } from "./sources";
import { getAccentColor } from "../../utils/helpers";
import styles from "./styles.module.css";

const HELP_TEXT =
  "هر پروژه، صفحه، ویو یا تسکی که زیاد بهش سر می‌زنی رو اینجا سنجاق کن تا با یک کلیک بازش کنی. " +
  "میان‌برها فقط برای خودت ذخیره می‌شن و روی همین مرورگر باقی می‌مونن. " +
  "با کشیدن هر کارت می‌تونی ترتیبشون رو عوض کنی و با تغییر تعداد ستون‌ها چیدمان رو به سلیقه‌ی خودت دربیاری.";

const ShortcutTile = ({ item, onOpen, onRemove }) => {
  const source = resolveSource(item.sourceKey);
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });

  return (
    <div
      ref={setNodeRef}
      className={`${styles.tile} ${isDragging ? styles.tileDragging : ""}`}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
      }}
      role="button"
      tabIndex={0}
      onClick={() => onOpen(item)}
      onKeyDown={(e) => e.key === "Enter" && onOpen(item)}
      {...attributes}
      {...listeners}
    >
      <span className={styles.tileGrip} aria-hidden="true">
        <HolderOutlined />
      </span>
      <span className={styles.tileIcon}>
        {item.avatar ? (
          <Avatar
            size={20}
            shape="square"
            src={item.avatar}
            icon={<AppstoreOutlined />}
          />
        ) : (
          source.icon
        )}
      </span>
      <span className={styles.tileLabel} title={item.label}>
        {item.label}
      </span>
      <Tooltip title="حذف میان‌بر">
        <button
          type="button"
          className={styles.remove}
          onClick={(e) => {
            e.stopPropagation();
            onRemove(item.id);
          }}
          onPointerDown={(e) => e.stopPropagation()}
        >
          <CloseOutlined />
        </button>
      </Tooltip>
    </div>
  );
};

const AddTile = ({ onClick, secondary }) => (
  <Tooltip title="افزودن میان‌بر">
    <button
      type="button"
      className={`${styles.addTile} ${secondary ? styles.addTileExtra : ""}`}
      onClick={onClick}
      aria-label="افزودن میان‌بر"
    >
      <PlusOutlined />
    </button>
  </Tooltip>
);

const QuickAccess = () => {
  const navigate = useNavigate();

  const orgId = useSelector(({ auth }) => auth.organization?.uuid);
  const userId = useSelector(({ auth }) => auth.user?.uuid);
  const scope = useMemo(() => ({ orgId, userId }), [orgId, userId]);

  const accent = useSelector(({ auth }) => getAccentColor(auth.organization));

  const [state, setState] = useState(() => loadQuickAccess(scope));
  const [isAdding, setIsAdding] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  );

  useEffect(() => {
    setState(loadQuickAccess(scope));
  }, [scope]);

  const persist = useCallback(
    (next) => {
      setState(next);
      saveQuickAccess(scope, next);
    },
    [scope],
  );

  const addItem = (item) =>
    persist({ ...state, items: [...state.items, item] });

  const removeItem = (id) =>
    persist({ ...state, items: state.items.filter((i) => i.id !== id) });

  const setColumns = (columns) => persist({ ...state, columns });

  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const from = state.items.findIndex((i) => i.id === active.id);
    const to = state.items.findIndex((i) => i.id === over.id);
    if (from === -1 || to === -1) return;
    persist({ ...state, items: arrayMove(state.items, from, to) });
  };

  const placeholders = useMemo(() => {
    if (state.items.length === 0) return state.columns;
    const remainder = state.items.length % state.columns;
    return remainder === 0 ? 1 : state.columns - remainder;
  }, [state.items.length, state.columns]);

  return (
    <div
      className={styles.panel}
      style={{ "--qa-accent": accent }}
      data-tour="quick-access"
    >
      <div className={styles.header}>
        <div className={styles.headerTitle}>
          <span className={styles.headerIcon}>
            <ThunderboltOutlined />
          </span>
          <span>دسترسی سریع</span>
          <Tooltip title={HELP_TEXT} placement="left">
            <QuestionCircleOutlined className={styles.help} />
          </Tooltip>
        </div>

        <div className={styles.headerActions}>
          <Segmented
            size="small"
            value={state.columns}
            onChange={setColumns}
            options={COLUMN_OPTIONS.map((c) => ({ label: `${c}`, value: c }))}
          />
        </div>
      </div>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <div className={styles.grid} style={{ "--qa-columns": state.columns }}>
          <SortableContext
            items={state.items.map((i) => i.id)}
            strategy={rectSortingStrategy}
          >
            {state.items.map((item) => (
              <ShortcutTile
                key={item.id}
                item={item}
                onOpen={(i) => navigate(i.url)}
                onRemove={removeItem}
              />
            ))}
          </SortableContext>

          {Array.from({ length: placeholders }).map((_, i) => (
            <AddTile
              key={`add-${i}`}
              secondary={i > 0}
              onClick={() => setIsAdding(true)}
            />
          ))}
        </div>
      </DndContext>

      <AddShortcutModal
        open={isAdding}
        onClose={() => setIsAdding(false)}
        onAdd={addItem}
        existingIds={state.items.map((i) => i.id)}
      />
    </div>
  );
};

export default QuickAccess;
